// Alignment guides + snap-on-release helpers.
// Sibling rects are captured once at dragstart and reused for the entire
// drag — no per-frame DOM thrashing.

export const VISUAL_RANGE = 4;
export const PIXEL_GRID_SIZE = 10;

const guideListeners = new Set();
let guideState = { active: false, guides: [], containerId: null };

export function subscribeDragGuides(cb) {
  guideListeners.add(cb);
  return () => guideListeners.delete(cb);
}

export function getDragGuides() {
  return guideState;
}

export function setDragGuides(guides, containerId) {
  guideState = { active: true, guides, containerId };
  guideListeners.forEach((l) => l(guideState));
}

export function clearDragGuides() {
  if (!guideState.active && guideState.guides.length === 0) return;
  guideState = { active: false, guides: [], containerId: guideState.containerId };
  guideListeners.forEach((l) => l(guideState));
}

let altPressed = false;
let listenersAttached = false;

function attachKeyboardListeners() {
  if (listenersAttached || typeof window === "undefined") return;
  listenersAttached = true;
  window.addEventListener(
    "keydown",
    (e) => {
      if (e.key === "Alt" || e.altKey) altPressed = true;
    },
    true
  );
  window.addEventListener(
    "keyup",
    (e) => {
      if (e.key === "Alt" || !e.altKey) altPressed = false;
    },
    true
  );
  window.addEventListener("blur", () => {
    altPressed = false;
  });
}
attachKeyboardListeners();

// Snapshot canvas + sibling-box edges/centers in container-local coords.
// `pixelGrid: true` enables grid-line snap on release.
export function buildSnapTargets({ boxes, draggedBoxId, containerId, pixelGrid = false }) {
  const container = document.getElementById(containerId);
  if (!container) return { targets: [], containerRect: null };

  const containerRect = container.getBoundingClientRect();
  const targets = [];

  targets.push(
    { axis: "v", pos: 0, kind: "canvas-edge" },
    { axis: "v", pos: containerRect.width / 2, kind: "canvas-center" },
    { axis: "v", pos: containerRect.width, kind: "canvas-edge" },
    { axis: "h", pos: 0, kind: "canvas-edge" },
    { axis: "h", pos: containerRect.height / 2, kind: "canvas-center" },
    { axis: "h", pos: containerRect.height, kind: "canvas-edge" }
  );

  const dragged = boxes?.[draggedBoxId];
  if (dragged) {
    Object.keys(boxes).forEach((id) => {
      if (id === draggedBoxId) return;
      const b = boxes[id];
      if (!b || b.parent !== dragged.parent) return;
      const el = document.getElementById(`box-${id}`);
      if (!el) return;
      const r = el.getBoundingClientRect();
      const left = r.left - containerRect.left;
      const right = r.right - containerRect.left;
      const top = r.top - containerRect.top;
      const bottom = r.bottom - containerRect.top;
      const cx = (left + right) / 2;
      const cy = (top + bottom) / 2;
      targets.push(
        { axis: "v", pos: left, kind: "box-edge", boxId: id },
        { axis: "v", pos: cx, kind: "box-center", boxId: id },
        { axis: "v", pos: right, kind: "box-edge", boxId: id },
        { axis: "h", pos: top, kind: "box-edge", boxId: id },
        { axis: "h", pos: cy, kind: "box-center", boxId: id },
        { axis: "h", pos: bottom, kind: "box-edge", boxId: id }
      );
    });
  }

  return { targets, containerRect, pixelGrid };
}

// Returns guide lines to draw. Always dx/dy = 0: snap is release-only.
export function resolveSnap({ draggedRect, snapData }) {
  if (altPressed || !snapData || !snapData.targets) {
    return { dx: 0, dy: 0, activeGuides: [] };
  }

  const { targets } = snapData;
  const left = draggedRect.left;
  const top = draggedRect.top;
  const right = left + draggedRect.width;
  const bottom = top + draggedRect.height;
  const cx = left + draggedRect.width / 2;
  const cy = top + draggedRect.height / 2;

  let bestV = null;
  let bestH = null;

  for (const t of targets) {
    if (t.axis === "v") {
      const candidates = [t.pos - left, t.pos - cx, t.pos - right];
      for (let i = 0; i < 3; i++) {
        const abs = Math.abs(candidates[i]) - (i === 1 ? 0.001 : 0);
        if (abs <= VISUAL_RANGE && (!bestV || abs < Math.abs(bestV.absDelta))) {
          bestV = { absDelta: abs, target: t };
        }
      }
    } else {
      const candidates = [t.pos - top, t.pos - cy, t.pos - bottom];
      for (let i = 0; i < 3; i++) {
        const abs = Math.abs(candidates[i]) - (i === 1 ? 0.001 : 0);
        if (abs <= VISUAL_RANGE && (!bestH || abs < Math.abs(bestH.absDelta))) {
          bestH = { absDelta: abs, target: t };
        }
      }
    }
  }

  const activeGuides = [];
  if (bestV) activeGuides.push({ axis: "v", pos: bestV.target.pos, kind: bestV.target.kind });
  if (bestH) activeGuides.push({ axis: "h", pos: bestH.target.pos, kind: bestH.target.kind });

  return { dx: 0, dy: 0, activeGuides };
}

// Priority: center > edge, canvas > box.
const RELEASE_PRIORITY = ["canvas-center", "canvas-edge", "box-center", "box-edge"];

// Snap at drag/resize release. First in-range target per axis (by priority)
// wins. If gridEnabled and an axis missed, fall back to pixel grid.
// Alt held = free mode, returns position unchanged.
export function snapOnRelease({ draggedRect, snapData, gridEnabled = false }) {
  if (altPressed) return { left: draggedRect.left, top: draggedRect.top };
  const left = draggedRect.left;
  const top = draggedRect.top;
  const right = left + draggedRect.width;
  const bottom = top + draggedRect.height;
  const cx = left + draggedRect.width / 2;
  const cy = top + draggedRect.height / 2;

  let dx = 0;
  let dy = 0;
  let xLocked = false;
  let yLocked = false;

  if (snapData && snapData.targets) {
    for (const priority of RELEASE_PRIORITY) {
      if (xLocked && yLocked) break;
      for (const t of snapData.targets) {
        if (t.kind !== priority) continue;
        if (t.axis === "v" && !xLocked) {
          const candidates = [t.pos - left, t.pos - cx, t.pos - right];
          let best = null;
          for (const c of candidates) {
            const abs = Math.abs(c);
            if (abs <= VISUAL_RANGE && (best === null || abs < Math.abs(best))) {
              best = c;
            }
          }
          if (best !== null) {
            dx = best;
            xLocked = true;
          }
        } else if (t.axis === "h" && !yLocked) {
          const candidates = [t.pos - top, t.pos - cy, t.pos - bottom];
          let best = null;
          for (const c of candidates) {
            const abs = Math.abs(c);
            if (abs <= VISUAL_RANGE && (best === null || abs < Math.abs(best))) {
              best = c;
            }
          }
          if (best !== null) {
            dy = best;
            yLocked = true;
          }
        }
      }
    }
  }

  if (gridEnabled) {
    if (!xLocked) dx = Math.round(left / PIXEL_GRID_SIZE) * PIXEL_GRID_SIZE - left;
    if (!yLocked) dy = Math.round(top / PIXEL_GRID_SIZE) * PIXEL_GRID_SIZE - top;
  }

  return { left: left + dx, top: top + dy };
}

// Alt held = free mode, returns rect unchanged.
export function snapResizeOnRelease({ rect, snapData, gridEnabled = false, movableEdges }) {
  if (altPressed) {
    return { left: rect.left, top: rect.top, width: rect.width, height: rect.height };
  }
  const edges = movableEdges || { left: true, right: true, top: true, bottom: true };
  let left = rect.left;
  let top = rect.top;
  let right = rect.left + rect.width;
  let bottom = rect.top + rect.height;

  const snapEdge = (pos, axis) => {
    if (!snapData || !snapData.targets) return null;
    for (const priority of RELEASE_PRIORITY) {
      let best = null;
      for (const t of snapData.targets) {
        if (t.kind !== priority || t.axis !== axis) continue;
        const d = t.pos - pos;
        const abs = Math.abs(d);
        if (abs <= VISUAL_RANGE && (best === null || abs < Math.abs(best))) {
          best = d;
        }
      }
      if (best !== null) return pos + best;
    }
    return null;
  };

  const snapToGrid = (pos) => Math.round(pos / PIXEL_GRID_SIZE) * PIXEL_GRID_SIZE;

  if (edges.left) {
    const s = snapEdge(left, "v");
    left = s !== null ? s : gridEnabled ? snapToGrid(left) : left;
  }
  if (edges.right) {
    const s = snapEdge(right, "v");
    right = s !== null ? s : gridEnabled ? snapToGrid(right) : right;
  }
  if (edges.top) {
    const s = snapEdge(top, "h");
    top = s !== null ? s : gridEnabled ? snapToGrid(top) : top;
  }
  if (edges.bottom) {
    const s = snapEdge(bottom, "h");
    bottom = s !== null ? s : gridEnabled ? snapToGrid(bottom) : bottom;
  }

  // Guard against inverted/zero sizes if both edges of an axis snapped past
  // each other.
  if (right < left + 1) right = left + 1;
  if (bottom < top + 1) bottom = top + 1;

  return { left, top, width: right - left, height: bottom - top };
}
